/**
 * Typed client for the V2 agent-executions list API
 * (`GET /api/v2/agent-executions` + `/filter-options`).
 *
 * The V2 wire format is camelCase (Constitution 2.3.6), but the existing
 * `AgentStatusTab` and the still-Python detail path consume the snake_case
 * `AgentExecutionListItem` shape. To keep churn minimal and avoid mixing cases
 * in one component, this client maps each V2 row back to that snake_case shape
 * and folds `sourceType` into `context: { source_type }` so the existing
 * `extractSourceType(execution.context, execution.agent_name)` badge keeps
 * working unchanged (and is now authoritative, not prefix-only).
 *
 * When detail/cancel/delete/retry migrate to V2 later, the component flips to
 * camelCase wholesale and this adapter can be dropped.
 */

import type { UUID } from "node:crypto";
import { v2Fetch } from "./client";
import type {
  AgentExecutionListItem,
  PaginatedAgentExecutionsResponse,
} from "@/app/api/types/agent-executions";

/** One row as returned by the V2 list endpoint (camelCase, per WIRE CONTRACT). */
interface V2Row {
  id: UUID;
  userId: UUID;
  companyId: UUID;
  groupId: UUID | null;
  agentType: string;
  agentName: string | null;
  status: number;
  errorMessage: string | null;
  summary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationSeconds: number | null;
  createdAt: string;
  updatedAt: string;
  sourceType: string | null;
}

interface V2ListResponse {
  items: V2Row[];
  total: number;
  limit: number;
  offset: number;
}

function mapRow(r: V2Row): AgentExecutionListItem {
  return {
    id: r.id,
    user_id: r.userId,
    company_id: r.companyId,
    group_id: r.groupId,
    agent_type: r.agentType,
    agent_name: r.agentName,
    status: r.status,
    error_message: r.errorMessage,
    summary: r.summary,
    started_at: r.startedAt,
    completed_at: r.completedAt,
    duration_seconds: r.durationSeconds,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    source_type: r.sourceType,
    // Fold sourceType into context so extractSourceType() stays authoritative.
    // The list is a lightweight projection: only `source_type` is forwarded —
    // any other context keys on the DB row are intentionally not carried here
    // (the still-Python detail path is the surface that exposes full context).
    context: r.sourceType ? { source_type: r.sourceType } : null,
  };
}

export interface ListAgentExecutionsParams {
  search?: string;
  channel?: string;
  agentType?: string;
  status?: number;
  /** UTC ISO start bound — callers convert with datetime helpers at the boundary. */
  from?: string;
  /** UTC ISO end bound. */
  to?: string;
  limit?: number;
  offset?: number;
}

/**
 * GET /api/v2/agent-executions — list agent executions for the authed company,
 * filtered by the combined name/ID search + date/channel/agent-type/status
 * filters. Returns the snake_case paginated shape the UI already consumes.
 */
export async function listAgentExecutionsV2(
  params: ListAgentExecutionsParams,
): Promise<PaginatedAgentExecutionsResponse> {
  const sp = new URLSearchParams();
  // Trim before forwarding: the V2 handler validates `search` with
  // `.trim().min(1)`, so a whitespace-only query would 400. Treat it as empty.
  const search = params.search?.trim();
  if (search) sp.set("search", search);
  if (params.channel) sp.set("channel", params.channel);
  if (params.agentType) sp.set("agentType", params.agentType);
  if (params.status !== undefined) sp.set("status", String(params.status));
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  sp.set("limit", String(params.limit ?? 50));
  sp.set("offset", String(params.offset ?? 0));

  const raw = await v2Fetch<V2ListResponse>(
    `/api/v2/agent-executions?${sp.toString()}`,
  );
  return {
    items: raw.items.map(mapRow),
    total: raw.total,
    limit: raw.limit,
    offset: raw.offset,
  };
}

/**
 * GET /api/v2/agent-executions/filter-options — distinct `agent_type` values
 * for the authed company, used to populate the agent-type filter dropdown.
 */
export async function getAgentExecutionFilterOptions(): Promise<{
  agentTypes: string[];
}> {
  return v2Fetch<{ agentTypes: string[] }>(
    `/api/v2/agent-executions/filter-options`,
  );
}
